import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useCallback, useMemo } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';

import {
    CompleteAccountModalContext,
    ModalContext,
    openGetHelpModal,
    openSendSellerMessageModal,
} from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { getCatalogDetailInHouseShipping } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCatalogIsLivePayments } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { GetHelpModalTypes } from '@/components/Modals/GetHelp/enums/GetHelpModal.enum';
import {
    getItemSummaryCatalogId,
    getItemSummaryLotNumber,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSellerInLiveShippingBeta } from '@/redux/modules/seller';
import { isItemPage } from '@/utils/urls';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTrackAskAQuestionClickedAnalytics } from '@/utils/amplitude/analytics/auctioneer/trackAskAQuestionClicked.analytics';
import FollowSellerButton from '@/components/FollowSellerButton/FollowSellerButton';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';

type Props = {
    itemId: number;
};

export const AuctioneerCTAs = ({ itemId }: Props) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const { pathname } = useLocation();

    // Get all the data we need from Redux
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const sellerUsesLiveShipping = useAppSelector((state) => getSellerInLiveShippingBeta(state, sellerId));
    const catalogIsLivePayments = useAppSelector((state) => getCatalogIsLivePayments(state, catalogId));
    const catalogOffersInHouseShipping = useAppSelector((state) => getCatalogDetailInHouseShipping(state, catalogId));

    const isOnItemPage = isItemPage(pathname);
    const trackAskAQuestionClickedAnalytics = useTrackAskAQuestionClickedAnalytics();

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.sendMessage,
            houseId: sellerId,
            id: itemId || sellerId,
            itemId,
            type: itemId
                ? CompleteAccountModalContext.AskQuestionItem
                : CompleteAccountModalContext.AskQuestionAuctioneer,
        }),
        [itemId, sellerId]
    );

    const showSendSellerMessageModal = useCallback(() => {
        const messageDetails = {
            catalogId,
            itemId,
            lotNumber,
            title,
        };

        const subject = formatMessage({ id: 'lot_number_and_title' }, { lotNumber, title });
        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [catalogId, dispatch, formatMessage, itemId, lotNumber, sellerId, sellerName, title]);

    const displayGetHelpModal = useCallback(() => {
        dispatch(
            openGetHelpModal({
                data: {
                    modalData: {
                        catalogId,
                        isLivePayments: catalogIsLivePayments,
                        isLiveShipping: sellerUsesLiveShipping && !catalogOffersInHouseShipping,
                        itemId,
                        sellerId,
                    },
                    modalVariant: GetHelpModalTypes.ItemPage,
                },
            })
        );
    }, [
        catalogId,
        catalogIsLivePayments,
        sellerUsesLiveShipping,
        catalogOffersInHouseShipping,
        itemId,
        sellerId,
        dispatch,
    ]);

    const onAskQuestionClicked = useCallback(() => {
        trackAskAQuestionClickedAnalytics();
        isOnItemPage ? displayGetHelpModal() : showSendSellerMessageModal();
    }, [displayGetHelpModal, isOnItemPage, showSendSellerMessageModal, trackAskAQuestionClickedAnalytics]);

    return (
        <div className="grid w-full grid-cols-2 gap-sm">
            <ShouldLogInContainer
                modalContext={modalContext}
                requireCompleteProfile
                requireLogin
                requireVerifiedAddress
                runFunction={onAskQuestionClicked}
            >
                <Button
                    data-testid="itemPageAskSellerQuestionButton"
                    size="sm"
                    variant="outlined"
                >
                    {formatMessage({
                        id: 'bidding_console.console.currentLiveItem.auctioneerInfo.buttons.askAQuestion',
                    })}
                </Button>
            </ShouldLogInContainer>
            <FollowSellerButton
                sellerId={sellerId}
                small
            />
        </div>
    );
};
